/**
 * featureFlags.ts
 *
 * Feature flags for GenesisBox. Two flag styles are supported:
 *
 * 1. **Env-controlled flags** (build-time, set via Vite env vars):
 *    - `OWNER_INLINE_VIEWER` — controls whether owner clicks on Gallery/Library
 *      mount the new inline ViewerPanel Drawer (true) or fall back to the
 *      legacy iframe path (false). Default: true.
 *    Set via `VITE_OWNER_INLINE_VIEWER=true|false` at build time.
 *
 * 2. **Per-request URL parameters** (runtime, set via query string):
 *    - `?legacyViewer=1` — forces iframe path even when env flag is true.
 *      Available on any /drive/... URL for 30 days post-merge as a debugging
 *      escape hatch. Useful when inline panel breaks for a specific
 *      folder/file type and we need to bypass without redeploying.
 *
 * Why two flags: env-flag controls global rollout; URL flag is per-session
 * opt-out for owners who hit a regression. URL flag wins over env flag.
 *
 * Removal plan: both flags removed after 30-day soak period if no rollback
 * issues observed in prod.
 */

/**
 * Reads the env-controlled OWNER_INLINE_VIEWER flag.
 * Defaults to true (new inline path on by default).
 */
export function isOwnerInlineViewerEnabled(): boolean {
  // Vite exposes env vars prefixed with VITE_ on import.meta.env
  const raw = import.meta.env.VITE_OWNER_INLINE_VIEWER;
  // Default to true: the inline path is the new norm. Set to "false" to
  // disable globally without redeploy if needed.
  if (raw === undefined || raw === '' || raw === null) return true;
  return String(raw).toLowerCase() !== 'false';
}

/**
 * Checks the current URL for the ?legacyViewer=1 escape hatch.
 *
 * @param search - The location.search string (e.g., "?legacyViewer=1&foo=bar").
 *                 Defaults to window.location.search if not provided.
 * @returns true if legacyViewer flag is present and truthy ("1", "true").
 */
export function isLegacyViewerForced(search?: string): boolean {
  const s = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  if (!s) return false;
  const params = new URLSearchParams(s);
  const v = params.get('legacyViewer');
  if (!v) return false;
  return v === '1' || v.toLowerCase() === 'true';
}

/**
 * The composite check used by DrivePage and other consumers.
 * Returns true when the inline ViewerPanel should be used.
 *
 * Logic: env flag AND NOT URL escape hatch.
 *
 * @param search - Optional location.search override (for testing).
 */
export function shouldUseInlineViewer(search?: string): boolean {
  return isOwnerInlineViewerEnabled() && !isLegacyViewerForced(search);
}
